/** @format */

import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
  MenuItem,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EditIcon from "@mui/icons-material/Edit";
import { toast } from "react-toastify";
import { getCfApprovalV2ById, getCfFinancialById } from "../cf-v2-create-form/api";
import CfV2EditDetails from "./cf-v2-edit-details";
import CfV2SkipStepDialog from "./cf-v2-skip-step-dialog";
import CreateStoreForCf from "./create-store-for-cf";
import CfV2FinancialTable from "./cf-v2-financial-table";
import CfV2StepHistory from "./cf-v2-step-history";
import CfV2SubmittedInputs, { ALWAYS_REPLACEABLE_FILES_BY_ROLE, REPLACEABLE_FILES_BY_STEP, isKeyUploadAllowed, isStepUploadEnabled } from "./cf-v2-submitted-inputs";
import CfV2StepInputForm, { validateStepInputs } from "./cf-v2-step-input-form";
import CfV2Attachments from "./cf-v2-attachments";
import ExceptionApprovalComments from "./exception-approval-comments";
import {
  invokeApi,
  invokeUploadFile,
  HTTP_METHODS,
} from "../../utils/http-service";

import { REST_URLS as INTRANET_URLS } from "../../../common-utilities/utils/end-points/intranet";
import { CF_APPROVAL_HOSTNAME, INTRANETHOSTNAME } from "../../../common-utilities/utils/end-points";
import { REST_URLS } from "../../../common-utilities/utils/end-points/cf-approval";
import { getOfflineData } from "../../utils/offline-services";
import { ATTACHMENT_FIELDS } from "../../utils/constants";
import { CF_APPROVAL_COMMON_ROUTE_V2 } from "../../../cf-approval/utils/constants";
import moment from "moment";
import { DIRECT_DISPLAY_STATUSES } from "../cf-v2-forms-status-tabs/v2-config";
import { getCfStatusChipSx, getCfStatusLabel } from "../../utils/constants";
import { getSignAndApproveConfig } from "../../utils/sign-and-approve-config";


const InfoTile = ({ label, value }) => (
  <Box sx={{ minWidth: 140, flex: "1 1 160px" }}>
    <Typography
      variant="caption"
      sx={{
        color: "text.secondary",
        fontWeight: 600,
        textTransform: "uppercase",
        letterSpacing: 0.4,
      }}
    >
      {label}
    </Typography>
    <Typography variant="body2" sx={{ fontWeight: 500, mt: 0.3 }}>
      {value ?? "—"}
    </Typography>
  </Box>
);

// Compose the display store code from its three parts (storeCode-sequenceCode-satelliteCode).
// Missing parts render as "X" (e.g. 12345-X-7). Legacy records keep the full "12345-6-7"
// string in storeCode alone, so return it as-is then.
const formatStoreCode = (form) => {
  const storeCode = form?.storeCode;
  if (String(storeCode || "").includes("-")) return storeCode;
  const parts = [storeCode, form?.sequenceCode, form?.satelliteCode];
  // Nothing entered at all → let InfoTile show its own placeholder.
  if (parts.every((p) => p == null || p === "")) return "";
  return parts.map((p) => (p == null || p === "" ? "X" : p)).join("-");
};

const isAuthorizedForStep = (user, stepMaster, cfRegion = null) => {
  if (!stepMaster || !user) return false;
  if (stepMaster.approverType === "USER") {
    return [stepMaster.approverUserId, ...(stepMaster.approverUserIds || [])].some((id) => id != null && +id === +user.id);
  }

  if (stepMaster.approverType === "REGIONAL_USER") {
    return (stepMaster.regionalApprovers || []).some(
      (ra) => ra.approverUserId === +user.id && (cfRegion ? ra.region === cfRegion : true)
    );
  }

  return stepMaster?.approverRole?.includes(user.role);
};

// Steps where missing documents can be waived via a unanimous parallel exception approval.
const EXCEPTION_APPROVAL_ENABLED_STEPS = [12, 13, 14, 15, 16];

const CfV2FormDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const currentUser = getOfflineData("user");

  const [form, setForm] = useState(null);
  const [stepMaster, setStepMaster] = useState(null);
  // Step Master list comes from redux (loaded once on app open).
  const allStepsMaster = useSelector((state) => state.cfApprovalV2.stepMaster) || [];
  const [formLoading, setFormLoading] = useState(true);
  const [actionModal, setActionModal] = useState(null); // 'approve' | 'drop'
  const [comment, setComment] = useState("");
  const [actionLoading, setActionLoading] = useState(false);
  const [sendToStep, setSendToStep] = useState("");
  const [uploadingKey, setUploadingKey] = useState(null);
  const [pendingUploadKey, setPendingUploadKey] = useState(null);
  const docFileInputRef = useRef(null);
  const [userDetailsMap, setUserDetailsMap] = useState({});
  const [financial, setFinancial] = useState(null);
  const [editDetailsOpen, setEditDetailsOpen] = useState(false);
  // Dynamic per-step inputs (dropdown selections + uploaded file URLs).
  const [stepInputValues, setStepInputValues] = useState({});
  const [inputUploadingKey, setInputUploadingKey] = useState(null);
  const inputFileInputRef = useRef(null);
  const pendingInputUploadRef = useRef(null); // { key, accept }
  // Step number whose already-submitted inputs are being edited/re-uploaded (post-approval).
  const [editStepNumber, setEditStepNumber] = useState(null);
  // In-place single-file replace (from a Submitted-Inputs card): the field key currently
  // uploading, the hidden picker, and the pending { key, storedAtStep, accept } target.
  const [replacingKey, setReplacingKey] = useState(null);
  const replaceFileInputRef = useRef(null);
  const pendingReplaceRef = useRef(null);

  // Roles permitted to skip a step via /move-steps (mirrors backend authorization).
  const canSkipStep = ["ADMIN", "BUSINESS_FINANCE"].includes(currentUser?.role?.toUpperCase());
  // ADMIN/IT may create+link the store-master store, until one is already linked (storeId set).
  const canCreateStore =
    ["ADMIN", "IT"].includes(currentUser?.role?.toUpperCase()) && !form?.storeId && form?.storeCode;

  const handleChange = (event) => {
    setSendToStep(event.target.value);
  };

  const resetActionState = () => {
    setActionModal(null);
    setComment("");
    setSendToStep("");
    setStepInputValues({});
    setEditStepNumber(null);
  };

  // Step master config for the step currently being edited (from redux step-master list).
  const editStepMaster = useMemo(
    () => (allStepsMaster || []).find((s) => s.stepNumber === Number(editStepNumber)) || null,
    [allStepsMaster, editStepNumber]
  );

  // Step 21's master, looked up independently of the form's CURRENT step — points can be
  // closed any time after step 21, even once the form has moved on.
  const step21Master = useMemo(
    () => (allStepsMaster || []).find((s) => s.stepNumber === 21) || null,
    [allStepsMaster]
  );

  // Step 21 snag-list points-closed: only offered once snagListStatus is 'Applicable', not
  // already closed, and only to step 21's approver (or admin).
  const canMarkPointsClosed = useMemo(() => {
    const isAdmin = currentUser?.role?.toUpperCase() === "ADMIN";
    return (
      form?.stepInputs?.["21"]?.snagListStatus === "Applicable" &&
      !form?.pointsClosed?.closed &&
      (isAdmin || isAuthorizedForStep(currentUser, step21Master, form?.address?.region))
    );
  }, [form, step21Master, currentUser]);

  // Steps whose submitted inputs the current user may edit/re-upload after the fact:
  // the step's master must allow it AND the user must be that step's approver (or an admin).
  // A step's own approver may edit it any time, even after the form has moved on.
  const editableStepNumbers = useMemo(() => {
    const isAdmin = currentUser?.role?.toUpperCase() === "ADMIN";
    const region = form?.address?.region;
    const result = (allStepsMaster || [])
      .filter(
        (s) =>
          s.allowInputEdit &&
          (s.inputFields || []).length &&
          (isAdmin || isAuthorizedForStep(currentUser, s, region))
      )
      .map((s) => s.stepNumber);

    return [...new Set(result)];
  }, [allStepsMaster, currentUser, form]);

  // Open the edit dialog for a step, pre-seeding its currently-submitted values.
  const handleEditStep = (stepNo) => {
    setEditStepNumber(stepNo);
    setStepInputValues({ ...(form?.stepInputs?.[String(stepNo)] || {}) });
    setActionModal("editInputs");
  };

  // Open the Points Closed dialog, pre-seeding the comment with step 21's own step-history
  // comment (e.g. the one left when snagListStatus was submitted as Applicable) so the closer
  // has that context without retyping it.
  const handleOpenPointsClosed = () => {
    const step21Comment = (form?.stepHistory || [])
      .filter((h) => h.stepNumber === 21 && h.comment)
      .map((h) => h.comment)
      .pop();
    setComment(step21Comment || "");
    setActionModal("pointsClosed");
  };

  const handleSaveStepInputs = () => {
    const fields = editStepMaster?.inputFields || [];
    const validationError = validateStepInputs(fields, stepInputValues);
    if (validationError) {
      toast.error(validationError);
      return;
    }
    setActionLoading(true);
    invokeApi(
      HTTP_METHODS.PUT,
      `${CF_APPROVAL_HOSTNAME}${REST_URLS.CF_V2}${id}/steps/${editStepNumber}/inputs`,
      { inputs: stepInputValues },
    )
      .then(() => {
        toast.success("Inputs updated successfully");
        resetActionState();
        loadFormDetails();
      })
      .catch((err) => toast.error(err?.message || "Failed to update inputs"))
      .finally(() => setActionLoading(false));
  };

  // Files the current user may replace in place, per REPLACEABLE_FILES_BY_STEP for the form's
  // CURRENT step — gated on the user being that step's approver (or admin). Maps
  // fieldKey -> { storedAtStep, accept }.
  const replaceableFieldsMap = useMemo(() => {
    const map = {};
    const isAdmin = currentUser?.role?.toUpperCase() === "ADMIN";
    // Role-based replacements work at any stage, once the file has actually been submitted.
    (ALWAYS_REPLACEABLE_FILES_BY_ROLE[currentUser?.role?.toUpperCase()] || []).forEach((f) => {
      if (!form?.stepInputs?.[String(f.storedAtStep)]?.[f.key]) return;
      map[f.key] = { storedAtStep: f.storedAtStep, accept: f.accept };
    });
    if (!isAdmin && !isAuthorizedForStep(currentUser, stepMaster, form?.address?.region)) {
      return map;
    }
    const stepNo = form?.currentStepDetails?.stepNumber || form?.currentStep || 0;
    (REPLACEABLE_FILES_BY_STEP[stepNo] || []).forEach((f) => {
      // Honor per-key locks: the step that STORES the file must not lock this key.
      const storedMaster = (allStepsMaster || []).find((s) => s.stepNumber === f.storedAtStep);
      if (!isKeyUploadAllowed(storedMaster?.editableFilesObject, f.key)) return;
      map[f.key] = { storedAtStep: f.storedAtStep, accept: f.accept };
    });
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stepMaster, currentUser, form, allStepsMaster]);

  // Open the file picker to replace a single file's contents (no step movement).
  const handleReplaceField = (fieldKey) => {
    const meta = replaceableFieldsMap[fieldKey];
    if (!meta) return;
    pendingReplaceRef.current = { key: fieldKey, ...meta };
    if (replaceFileInputRef.current) {
      replaceFileInputRef.current.accept = meta.accept || "";
      replaceFileInputRef.current.value = null;
      replaceFileInputRef.current.click();
    }
  };

  // Upload the picked file, then swap it in place via the dedicated replace-file endpoint.
  const handleReplaceFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    e.target.value = null;
    const pending = pendingReplaceRef.current;
    if (!pending) return;
    const { key, accept, storedAtStep } = pending;
    if (accept && file.type) {
      const allowed = accept.split(",").map((s) => s.trim());
      if (!allowed.includes(file.type)) {
        toast.error(`Invalid file format. Accepted: ${accept}`);
        return;
      }
    }
    const formData = new FormData();
    formData.append("upload", file);
    setReplacingKey(key);
    invokeUploadFile(`${CF_APPROVAL_HOSTNAME}${REST_URLS.UPLOAD_FILES}`, formData)
      .then((res) => {
        const url = res.data[0].split("?")[0];
        return invokeApi(
          HTTP_METHODS.PUT,
          `${CF_APPROVAL_HOSTNAME}${REST_URLS.CF_V2}${id}/replace-file`,
          { stepNumber: storedAtStep, key, url },
        );
      })
      .then(() => {
        toast.success("File replaced successfully");
        loadFormDetails();
      })
      .catch((err) => toast.error(err?.message || "Failed to replace file"))
      .finally(() => setReplacingKey(null));
  };

  // `onUploaded(url)` (optional) routes the resolved URL somewhere other than the flat
  // value map — e.g. into a REPEATABLE field's entry at a given index.
  const triggerStepInputUpload = (key, accept, onUploaded) => {
    pendingInputUploadRef.current = { key, accept, onUploaded };
    if (inputFileInputRef.current) {
      inputFileInputRef.current.accept = accept || "";
      inputFileInputRef.current.value = null;
      inputFileInputRef.current.click();
    }
  };

  const handleStepInputFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    e.target.value = null;
    const pending = pendingInputUploadRef.current;
    if (!pending) return;
    const { key, accept, onUploaded } = pending;
    if (accept && file.type) {
      const allowed = accept.split(",").map((s) => s.trim());
      if (!allowed.includes(file.type)) {
        toast.error(`Invalid file format. Accepted: ${accept}`);
        return;
      }
    }
    const formData = new FormData();
    formData.append("upload", file);
    setInputUploadingKey(key);
    invokeUploadFile(`${CF_APPROVAL_HOSTNAME}${REST_URLS.UPLOAD_FILES}`, formData)
      .then((res) => {
        const url = res.data[0].split("?")[0];
        if (typeof onUploaded === "function") {
          onUploaded(url);
        } else {
          setStepInputValues((prev) => ({ ...prev, [key]: url }));
        }
        toast.success("File uploaded");
      })
      .catch(() => toast.error("Failed to upload file"))
      .finally(() => setInputUploadingKey(null));
  };

  const loadUsers = () => {
    invokeApi(
      HTTP_METHODS.GET,
      `${INTRANETHOSTNAME}${INTRANET_URLS.FILTER_USER}`,
      null,
      { appLink: "cf-approval-v2", page: 1, limit: 100 },
    ).then((res) => {
      if (Array.isArray(res?.results)) {
        const map = {};
        res.results.forEach((user) => {
          map[user.id] = user.name;
        });
        setUserDetailsMap(map);
      }
    });
  }


  const handleApprove = () => {
    const fields = stepMaster?.inputFields || [];
    const validationError = validateStepInputs(fields, stepInputValues);
    if (validationError) {
      toast.error(validationError);
      return;
    }
    setActionLoading(true);
    const payload = { comment };
    if (fields.length && Object.keys(stepInputValues).length) {
      payload.inputs = stepInputValues;
    }
    invokeApi(
      HTTP_METHODS.POST,
      `${CF_APPROVAL_HOSTNAME}${REST_URLS.CF_V2}${id}/approve`,
      payload,
    )
      .then(() => {
        toast.success("CF Form approved successfully");
        resetActionState();
        loadFormDetails();
      })
      .catch((err) => toast.error(err?.message || "Failed to approve CF Form"))
      .finally(() => setActionLoading(false));
  };

  const handleReassign = () => {
    setActionLoading(true);
    const payload = { comment, sendToStep };
    invokeApi(
      HTTP_METHODS.POST,
      `${CF_APPROVAL_HOSTNAME}${REST_URLS.CF_V2}${id}/reassign`,
      payload,
    )
      .then(() => {
        toast.success("CF Form reassigned successfully");
        resetActionState();
        loadFormDetails();
      })
      .catch((err) => toast.error(err?.message || "Failed to reassign CF Form"))
      .finally(() => setActionLoading(false));
  };

  // Skip exactly one step forward from the current step via the /move-steps endpoint,
  // recording the reason file + comment on the SKIPPED step-history entry.
  const handleSkipStep = ({ comment: skipComment, skipedReasonDoc } = {}) => {
    setActionLoading(true);
    const payload = {
      cfIds: [Number(id)],
      toStep: currentStepNo + 1,
      skipedReasonDoc: skipedReasonDoc || undefined,
      comment: skipComment || undefined,
      skip: true,
    };
    invokeApi(
      HTTP_METHODS.POST,
      `${CF_APPROVAL_HOSTNAME}${REST_URLS.CF_V2_MOVE_STEPS}`,
      payload,
    )
      .then((res) => {
        if (res?.failedCount) {
          toast.error(res.failed?.[0]?.reason || "Failed to skip step");
          return;
        }
        toast.success("Step skipped successfully");
        resetActionState();
        loadFormDetails();
      })
      .catch((err) => toast.error(err?.message || "Failed to skip step"))
      .finally(() => setActionLoading(false));
  };

  const handleSaveDetails = ({ siteName, storeCode, sequenceCode, satelliteCode, region, circle, city }) => {
    setActionLoading(true);
    invokeApi(
      HTTP_METHODS.PUT,
      `${CF_APPROVAL_HOSTNAME}${REST_URLS.CF_V2}${id}/details`,
      { siteName, storeCode, sequenceCode, satelliteCode, address: { region, circle, city } },
    )
      .then(() => {
        toast.success("Details updated successfully");
        setEditDetailsOpen(false);
        loadFormDetails();
      })
      .catch((err) => toast.error(err?.message || "Failed to update details"))
      .finally(() => setActionLoading(false));
  };

  const handleMarkPointsClosed = () => {
    setActionLoading(true);
    invokeApi(
      HTTP_METHODS.PUT,
      `${CF_APPROVAL_HOSTNAME}${REST_URLS.CF_V2}${id}/points-closed`,
      { comment },
    )
      .then(() => {
        toast.success("Points closed successfully");
        resetActionState();
        loadFormDetails();
      })
      .catch((err) => toast.error(err?.message || "Failed to close points"))
      .finally(() => setActionLoading(false));
  };

  const handleRequestExceptionApproval = () => {
    setActionLoading(true);
    invokeApi(
      HTTP_METHODS.POST,
      `${CF_APPROVAL_HOSTNAME}${REST_URLS.CF_V2}${id}/exception-approval/request`,
      { comment },
    )
      .then(() => {
        toast.success("Exception approval requested");
        resetActionState();
        loadFormDetails();
      })
      .catch((err) => toast.error(err?.message || "Failed to request exceptional approval"))
      .finally(() => setActionLoading(false));
  };

  // `decision` is 'APPROVED' | 'REJECTED' — set by which button opened the dialog.
  const handleRespondExceptionApproval = (decision) => {
    setActionLoading(true);
    invokeApi(
      HTTP_METHODS.POST,
      `${CF_APPROVAL_HOSTNAME}${REST_URLS.CF_V2}${id}/exception-approval/respond`,
      { decision, comment: comment || undefined },
    )
      .then(() => {
        toast.success(decision === "APPROVED" ? "Exception approved" : "Exception rejected");
        resetActionState();
        loadFormDetails();
      })
      .catch((err) => toast.error(err?.message || "Failed to respond to exception approval"))
      .finally(() => setActionLoading(false));
  };

  const handleDrop = () => {
    setActionLoading(true);
    let payload = {};
    if (comment) {
      payload.comment = comment;
    }
    invokeApi(
      HTTP_METHODS.POST,
      `${CF_APPROVAL_HOSTNAME}${REST_URLS.CF_V2}${id}/drop`,
      payload,
    )
      .then(() => {
        toast.success("CF Form dropped successfully");
        setActionModal(null);
        loadFormDetails();
      })
      .catch((err) => toast.error(err?.message || "Failed to drop CF Form"))
      .finally(() => setActionLoading(false));
  };

  const loadFormDetails = () => {
    setFormLoading(true);
    getCfApprovalV2ById(id)
      .then((data) => {
        setForm(data);
        setStepMaster(data?.currentStepDetails || {});
      })
      .catch(() => toast.error("Failed to load form details"))
      .finally(() => setFormLoading(false));
    getCfFinancialById(id)
      .then((data) => setFinancial(data))
      .catch(() => setFinancial(null))
      .finally(() => setFormLoading(false));
  };

  useEffect(() => {
    setFormLoading(true);
    loadUsers();
    loadFormDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);


  const handleDocumentReupload = (key) => {
    const field = ATTACHMENT_FIELDS.find((f) => f.key === key);
    if (field) docFileInputRef.current.accept = field.accept;
    setPendingUploadKey(key);
    docFileInputRef.current.click();
  };

  const handleDocFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    e.target.value = null;
    const key = pendingUploadKey;
    const field = ATTACHMENT_FIELDS.find((f) => f.key === key);
    if (field && file.type && !field.fileFormats.includes(file.type)) {
      toast.error(`Invalid file format for ${field.label}. Accepted: ${field.accept}`);
      return;
    }
    const formData = new FormData();
    formData.append("upload", file);
    setUploadingKey(key);
    invokeUploadFile(`${CF_APPROVAL_HOSTNAME}${REST_URLS.UPLOAD_FILES}`, formData)
      .then((res) => {
        const url = res.data[0].split("?")[0];
        return invokeApi(
          HTTP_METHODS.PUT,
          `${CF_APPROVAL_HOSTNAME}${REST_URLS.CF_V2}${id}/documents`,
          { attachments: { [key]: url } },
        );
      })
      .then(() => {
        loadFormDetails();
        toast.success("Document updated successfully");
      })
      .then((updated) => {
        setForm(updated);
      })
      .catch((err) => toast.error(err?.message || "Failed to upload document"))
      .finally(() => setUploadingKey(null));
  };


  const stepNumberNameMap = useMemo(() => {
    if (!allStepsMaster) return {};
    return allStepsMaster.reduce((acc, step) => {
      acc[step.stepNumber] = step.name;
      return acc;
    }, {});
  }, [allStepsMaster]);

  // stepNumber -> { fieldKey: label } for rendering captured stepInputs read-only.
  const inputFieldLabelMap = useMemo(() => {
    const map = {};
    (allStepsMaster || []).forEach((s) => {
      map[s.stepNumber] = {};
      (s.inputFields || []).forEach((f) => {
        map[s.stepNumber][f.key] = f.label || f.key;
        if (f.fileUpload) {
          const fk = f.fileUpload.key || `${f.key}File`;
          map[s.stepNumber][fk] = f.fileUpload.label || f.label || fk;
        }
      });
    });
    return map;
  }, [allStepsMaster]);

  const currentStepNo = form?.currentStepDetails?.stepNumber || form?.currentStep || 0;
  const signAndApproveConfig = getSignAndApproveConfig(currentStepNo);
  // BUSINESS_FINANCE keeps the ability to re-upload CF attachments for the lifetime of the
  // case once it's past step 11 (CEO sign-off), independent of later steps' own upload gates.
  const businessFinanceAlwaysUpload =
    currentUser?.role?.toUpperCase() === "BUSINESS_FINANCE" && currentStepNo > 11;

  // Highest active step — a skip is only possible while a next step exists.
  const lastActiveStep = useMemo(() => {
    const active = (allStepsMaster || []).filter((s) => s.isActive).map((s) => s.stepNumber);
    return active.length ? Math.max(...active) : 0;
  }, [allStepsMaster]);

  // Shared props for the tabbed step-input form (used by both Approve and Edit dialogs).
  const stepInputFormProps = {
    values: stepInputValues,
    onChange: (key, value) => setStepInputValues((prev) => ({ ...prev, [key]: value })),
    onUploadFile: (fileKey, accept, onUploaded) => triggerStepInputUpload(fileKey, accept, onUploaded),
    uploadingKey: inputUploadingKey,
  };

  const getApprovedDetails = () => {

    if (form?.currentStepDetails?.approverType === "USER") {

      const userIds = [...new Set([form?.currentStepDetails?.approverUserId, ...(form?.currentStepDetails?.approverUserIds || [])].filter((id) => id != null))];
      return userIds.length ? userIds.map((id) => userDetailsMap[id] || id).join(" / ") : "N/A";

    } else if (form?.currentStepDetails?.approverType === "REGIONAL_USER") {
      const regionalApprover = (form?.currentStepDetails?.regionalApprovers || []).find(ra => ra.region === form?.address?.region);
      return regionalApprover ? userDetailsMap[regionalApprover.approverUserId] || regionalApprover.approverUserId : "N/A";
    }
    if (form?.currentStepDetails?.approverRole.includes("BD_LEAD")) {
      return userDetailsMap[form.createdBy] || form.createdBy || "-";
    }
    return form?.currentStepDetails?.approverRole.join(", ")
  }

  // Steps 12-16, in progress, and requestable only by LEGAL_TEAM — independent of whichever
  // role normally approves the current step.
  const canRequestExceptionApproval =
    EXCEPTION_APPROVAL_ENABLED_STEPS.includes(currentStepNo) &&
    form?.status === "IN_PROGRESS" &&
    currentUser?.role?.toUpperCase() === "LEGAL_TEAM"

  // Independent of currentStepNo/isAuthorizedForStep — an exception approver isn't the step-13
  // owner, so this must check the snapshotted approvers list on the record directly.
  const myExceptionEntry = (form?.exceptionApproval?.approvers || []).find(
    (a) => +a.userId === +currentUser?.id
  );
  const canRespondToException =
    form?.status === "EXCEPTION_PENDING" && myExceptionEntry && myExceptionEntry.decision == null;

  return (
    <Box sx={{ p: 3 }}>
      {/* Hidden input used for all dynamic step-input file uploads (approve + reassign) */}
      <input
        ref={inputFileInputRef}
        type="file"
        style={{ display: "none" }}
        onChange={handleStepInputFileChange}
      />
      {/* Hidden input used for in-place single-file replace from Submitted-Inputs cards */}
      <input
        ref={replaceFileInputRef}
        type="file"
        style={{ display: "none" }}
        onChange={handleReplaceFileChange}
      />
      {/* Page Header */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 3 }}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <IconButton onClick={() => navigate(CF_APPROVAL_COMMON_ROUTE_V2)} size="small">
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" fontWeight={700}>
            CF Form Details
            {form && (
              <Typography
                component="span"
                variant="body2"
                color="text.secondary"
                sx={{ ml: 1 }}
              >
                #{form.id || form._id}
              </Typography>
            )}
          </Typography>
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center">
          {['IN_PROGRESS'].includes(form?.status) &&
            (isAuthorizedForStep(currentUser, stepMaster, form?.address?.region) || canSkipStep) && (
              <Stack direction="row" spacing={1}>
                {isAuthorizedForStep(currentUser, stepMaster, form?.address?.region) &&
                  !signAndApproveConfig?.hideApproveButton && (
                    <Button
                      variant="contained"
                      size="small"
                      onClick={() => setActionModal("approve")}
                    >
                      Approve
                    </Button>
                  )}
                {isAuthorizedForStep(currentUser, stepMaster, form?.address?.region) &&
                  stepMaster?.stepNumber > 1 && stepMaster?.canSendBackTo?.length > 0 && (
                    <Button
                      variant="contained"
                      color="info"
                      size="small"
                      onClick={() => {
                        if (stepMaster?.canSendBackTo?.length === 1) {
                          setSendToStep(stepMaster.canSendBackTo[0]);
                        }
                        setActionModal("reassign");
                      }}
                    >
                      Reassign
                    </Button>
                  )}
                {isAuthorizedForStep(currentUser, stepMaster, form?.address?.region) &&
                  ["BD_LEAD", "BD_REGIONAL_HEAD"].includes(currentUser?.role?.toUpperCase()) && (
                    <Button
                      variant="contained"
                      color="error"
                      size="small"
                      onClick={() => {
                        setActionModal("drop");
                      }}
                    >
                      Drop
                    </Button>
                  )}
              </Stack>
            )}
          {/* Create Store — ADMIN/IT, next to Skip Step; shown for any status until a store is linked. */}
          {canCreateStore &&
            <Button
              variant="contained"
              size="small"
              onClick={() => setActionModal("createStore")}
            >
              Create Store
            </Button>
          }
          {signAndApproveConfig &&
            isAuthorizedForStep(currentUser, stepMaster, form?.address?.region) && (
              <Button
                variant="contained"
                size="small"
                onClick={() => navigate(`${CF_APPROVAL_COMMON_ROUTE_V2}/affix-signature/${id}`)}
              >
                {signAndApproveConfig.buttonLabel}
              </Button>
            )}
          {canRequestExceptionApproval && (
            <Button
              variant="outlined"
              color="warning"
              size="small"
              onClick={() => setActionModal("requestException")}
            >
              Exceptional Approval Request
            </Button>
          )}
          {/* Skip Step — ADMIN / BUSINESS_FINANCE, moves the form forward exactly one step. */}
          {canSkipStep && currentStepNo < lastActiveStep && (
            <Button
              variant="contained"
              color="warning"
              size="small"
              onClick={() => setActionModal("skip")}
            >
              Skip Step
            </Button>
          )}
          {/* Capex Calculator — attached here (the form-details wrapper) rather than a single
              step, since it's a per-CF capital-expenditure worksheet usable at any point in the
              approval flow, not tied to one particular step's inputs. */}
          <Button
            variant="outlined"
            size="small"
            onClick={() => navigate(`${CF_APPROVAL_COMMON_ROUTE_V2}/capex-calculator/${id}`)}
          >
            Capex Calculator
          </Button>
        </Stack>
      </Stack>

      {formLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", pt: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {form?.status === "EXCEPTION_PENDING" && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <Typography variant="body2">
                Exception approval requested by{" "}
                {userDetailsMap[form.exceptionApproval.requestedBy] || form.exceptionApproval.requestedBy}
                {form.exceptionApproval.requestedAt
                  ? ` on ${moment(form.exceptionApproval.requestedAt).format("DD MMM YYYY")}`
                  : ""}
                : &quot;{form.exceptionApproval.requestComment}&quot;
              </Typography>
              <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                {(form.exceptionApproval.approvers || [])
                  .map((a) => `${userDetailsMap[a.userId] || a.userId}: ${a.decision || "Pending"}`)
                  .join(" · ")}
              </Typography>
              <Stack direction="row" spacing={1} sx={{ mt: 1 }} alignItems="center">
                {canRespondToException && (
                  <>
                    <Button
                      size="small"
                      variant="contained"
                      onClick={() => setActionModal("respondExceptionApprove")}
                    >
                      Approve
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      color="error"
                      onClick={() => setActionModal("respondExceptionReject")}
                    >
                      Reject
                    </Button>
                  </>
                )}
                {/* For now, only while a request is outstanding — we can widen this to other
                    exceptionApproval.status values (e.g. APPROVED) later. */}
                {form?.exceptionApproval?.status === "PENDING" && <ExceptionApprovalComments id={id} />}
              </Stack>
            </Alert>
          )}
          {/* Top Section — 50 / 50, Step History full width below */}
          <Grid container spacing={2}>
            {/* Site Details — 50% */}
            <Grid item xs={12} md={6}>
              <Paper variant="outlined" sx={{ p: 2, height: "100%" }}>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ mb: 2 }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{
                      color: "#008938",
                      fontWeight: 700,
                      textTransform: "uppercase",
                      letterSpacing: 0.5,
                    }}
                  >
                    Site Details
                  </Typography>
                  {["APPROVED", "IN_PROGRESS"].includes(form?.status) &&
                    !form?.storeId &&
                    currentUser?.role?.toUpperCase() === "BUSINESS_FINANCE" && (
                      <IconButton
                        size="small"
                        onClick={() => setEditDetailsOpen(true)}
                        title="Edit details"
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    )}
                </Stack>

                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
                  <InfoTile label="Site Name" value={form?.siteName} />
                  <InfoTile label="Store Code" value={formatStoreCode(form)} />
                  <Box sx={{ minWidth: 140, flex: "1 1 160px" }}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: "text.secondary",
                        fontWeight: 600,
                        textTransform: "uppercase",
                        letterSpacing: 0.4,
                      }}
                    >
                      Status
                    </Typography>
                    <Box sx={{ mt: 0.3 }}>
                      {form?.status ? (
                        <Chip
                          label={getCfStatusLabel(DIRECT_DISPLAY_STATUSES.includes(form.status) ? form.status : form?.currentStepDetails?.cfStatus)}
                          size="small"
                          variant="outlined"
                          sx={getCfStatusChipSx(DIRECT_DISPLAY_STATUSES.includes(form.status) ? form.status : form?.currentStepDetails?.cfStatus)}
                        />
                      ) : (
                        <Typography variant="body2">—</Typography>
                      )}
                    </Box>
                  </Box>
                  <InfoTile label="Region" value={form?.address?.region} />
                  <InfoTile label="Circle" value={form?.address?.circle} />
                  <InfoTile label="City" value={form?.address?.city} />
                  <InfoTile
                    label="Relocated"
                    value={form?.relocated ? "Yes" : "No"}
                  />
                  <InfoTile
                    label="Part of AOP Approved List"
                    value={form?.partOfAOP ? "Yes" : "No"}
                  />
                  <InfoTile label="Current Step" value={`Step ${form?.currentStepDetails?.stepNumber} - ${form?.currentStepDetails?.name}`} />
                  {/* <InfoTile label="Approver Type" value={form?.currentStepDetails?.approverType} /> */}

                  <InfoTile label="Approver" value={getApprovedDetails()} />
                  <InfoTile
                    label="Created At"
                    value={moment(form?.createdAt).format("DD MMM YYYY, hh:mm A") || "—"}
                  />
                  {form?.storeId && <InfoTile label="Store ID" value={form.storeId} />}
                </Box>
                <CfV2Attachments
                  form={form}
                  userDetailsMap={userDetailsMap}
                  uploadingKey={uploadingKey}
                  canUpload={
                    // BUSINESS_FINANCE can always re-upload the CF attachments once the case is past
                    // step 11 (CEO sign-off), regardless of the current step's own editableFilesObject
                    // (from step 12 onward that map governs different, step-specific documents).
                    businessFinanceAlwaysUpload ||
                    // Step/user gate: attachments are editable only on upload-enabled steps (1–7) —
                    // those carrying an editableFilesObject with at least one editable key — and only
                    // by the current step's approver (or an admin override). The per-key lock is then
                    // applied inside CfV2Attachments via editableFilesObject, consistent with the
                    // step-input replace/edit paths.
                    (isStepUploadEnabled(form?.currentStepDetails?.editableFilesObject) &&
                    (["ADMIN", "BUSINESS_FINANCE"].includes(currentUser?.role?.toUpperCase()) ||
                      isAuthorizedForStep(currentUser, stepMaster, form?.address?.region)))
                  }
                  bypassKeyLock={businessFinanceAlwaysUpload}
                  editableFilesObject={form?.currentStepDetails?.editableFilesObject}
                  fileInputRef={docFileInputRef}
                  onFileChange={handleDocFileChange}
                  onReupload={handleDocumentReupload}
                />

                {/* Submitted Inputs — below Attachments, only after step 12 */}
                <CfV2SubmittedInputs
                  stepInputs={form?.stepInputs}
                  inputFieldLabelMap={inputFieldLabelMap}
                  stepNameMap={stepNumberNameMap}
                  currentStep={currentStepNo}
                  editableStepNumbers={editableStepNumbers}
                  onEditStep={handleEditStep}
                  replaceableFields={replaceableFieldsMap}
                  onReplaceField={handleReplaceField}
                  replacingFieldKey={replacingKey}
                  pointsClosed={form?.pointsClosed}
                  canMarkPointsClosed={canMarkPointsClosed}
                  onMarkPointsClosed={handleOpenPointsClosed}
                />
              </Paper>
            </Grid>

            {/* Financial Data — 50% */}
            <Grid item xs={12} md={6}>
              <CfV2FinancialTable formId={id} financial={financial} />
            </Grid>

            {/* Step History — 100% */}
            <Grid item xs={12} sx={{ mt: 3 }}>
              <CfV2StepHistory
                stepHistory={form?.stepHistory || []}
                stepNumberNameMap={stepNumberNameMap}
                userDetailsMap={userDetailsMap}
              />
            </Grid>
          </Grid>
        </>
      )}

      {/* Approve Dialog */}
      <Dialog
        open={actionModal === "approve"}
        onClose={() => setActionModal(null)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Approve CF Form</DialogTitle>
        <DialogContent>
          {!!(stepMaster?.inputFields || []).length && (
            <Box sx={{ mb: 1 }}>
              <Typography
                variant="caption"
                sx={{ color: "text.secondary", fontWeight: 600, textTransform: "uppercase" }}
              >
                Required Inputs
              </Typography>
              <CfV2StepInputForm
                stepNumber={stepMaster.stepNumber}
                fields={stepMaster.inputFields}
                {...stepInputFormProps}
              />
            </Box>
          )}
          <TextField
            label="Comment (optional)"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            fullWidth
            multiline
            rows={6}
            size="small"
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={resetActionState}
            disabled={actionLoading}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleApprove}
            disabled={actionLoading}
          >
            {actionLoading ? <CircularProgress size={18} /> : "Approve"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Inputs Dialog — update/re-upload a step's already-submitted inputs */}
      <Dialog
        open={actionModal === "editInputs"}
        onClose={resetActionState}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          {`Edit Inputs — Step ${editStepNumber}${stepNumberNameMap[editStepNumber] ? ` - ${stepNumberNameMap[editStepNumber]}` : ""
            }`}
        </DialogTitle>
        <DialogContent>
          {!!(editStepMaster?.inputFields || []).length ? (
            <CfV2StepInputForm
              stepNumber={editStepMaster.stepNumber}
              fields={editStepMaster.inputFields}
              lockEditableFiles={editStepMaster.editableFilesObject}
              {...stepInputFormProps}
            />
          ) : (
            <Typography variant="body2" color="text.secondary">
              No editable inputs for this step.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={resetActionState} disabled={actionLoading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveStepInputs}
            disabled={actionLoading || !(editStepMaster?.inputFields || []).length}
          >
            {actionLoading ? <CircularProgress size={18} /> : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Drop Dialog */}
      <Dialog
        open={actionModal === "drop"}
        onClose={() => setActionModal(null)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Drop CF Form</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Are you sure you want to drop this CF form?
          </Typography>
          <TextField
            label="Comment (optional)"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            fullWidth
            multiline
            rows={6}
            size="small"
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setActionModal(null)} disabled={actionLoading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDrop}
            disabled={actionLoading || !comment?.trim()}
          >
            {actionLoading ? <CircularProgress size={18} /> : "Drop"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Points Closed Dialog — Step 21 snag-list follow-up, comment required */}
      <Dialog
        open={actionModal === "pointsClosed"}
        onClose={resetActionState}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Mark Points Closed</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            This marks the snag list points as closed for this CF form.
          </Typography>
          <TextField
            label="Comment"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            fullWidth
            multiline
            rows={6}
            size="small"
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={resetActionState} disabled={actionLoading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleMarkPointsClosed}
            disabled={actionLoading || !comment?.trim()}
          >
            {actionLoading ? <CircularProgress size={18} /> : "Mark Closed"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Exceptional Approval Request Dialog — Step 13, comment required, goes out in the request email */}
      <Dialog
        open={actionModal === "requestException"}
        onClose={resetActionState}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Exceptional Approval Request</DialogTitle>
        <DialogContent>
          <TextField
            label="Comment (required)"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            fullWidth
            multiline
            rows={6}
            size="small"
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={resetActionState} disabled={actionLoading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleRequestExceptionApproval}
            disabled={actionLoading || !comment?.trim()}
          >
            {actionLoading ? <CircularProgress size={18} /> : "Send Request"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Respond to Exception Approval — Approve/Reject variants, comment optional */}
      <Dialog
        open={actionModal === "respondExceptionApprove" || actionModal === "respondExceptionReject"}
        onClose={resetActionState}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          {actionModal === "respondExceptionApprove" ? "Approve Exception" : "Reject Exception"}
        </DialogTitle>
        <DialogContent>
          <TextField
            label="Comment (optional)"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            fullWidth
            multiline
            rows={6}
            size="small"
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={resetActionState} disabled={actionLoading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color={actionModal === "respondExceptionReject" ? "error" : "primary"}
            onClick={() =>
              handleRespondExceptionApproval(actionModal === "respondExceptionApprove" ? "APPROVED" : "REJECTED")
            }
            disabled={actionLoading}
          >
            {actionLoading ? (
              <CircularProgress size={18} />
            ) : actionModal === "respondExceptionApprove" ? (
              "Approve"
            ) : (
              "Reject"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      <CfV2EditDetails
        open={editDetailsOpen}
        initialValues={{
          siteName: form?.siteName,
          storeCode: form?.storeCode,
          sequenceCode: form?.sequenceCode,
          satelliteCode: form?.satelliteCode,
          region: form?.address?.region,
          circle: form?.address?.circle,
          city: form?.address?.city,
          cfStatus: form?.currentStepDetails?.cfStatus
        }}
        onClose={() => setEditDetailsOpen(false)}
        onSave={handleSaveDetails}
        loading={actionLoading}
      />

      {/* ReAssign Dialog */}
      <Dialog
        open={actionModal === "reassign"}
        onClose={() => setActionModal(null)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Reassign CF Form</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Are you sure you want to reassign this CF form?
          </Typography>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel id="send-to-step-select-label" size="small">
              Send to step
            </InputLabel>
            <Select
              labelId="send-to-step-select-label"
              id="send-to-step-select"
              value={sendToStep}
              label="Send to step"
              size="small"
              onChange={handleChange}
            >
              {stepMaster?.canSendBackTo?.map((step) => (
                <MenuItem key={step} value={step}>
                  Step {step} ({stepNumberNameMap[step] || "Unknown"})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="Comment (optional)"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            fullWidth
            multiline
            rows={6}
            size="small"
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={resetActionState} disabled={actionLoading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="info"
            onClick={handleReassign}
            disabled={actionLoading || !comment?.trim() || !sendToStep}
          >
            {actionLoading ? <CircularProgress size={18} /> : "Reassign"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Skip Step Dialog — advances the form one step, capturing a reason file + comment */}
      <CfV2SkipStepDialog
        open={actionModal === "skip"}
        onClose={resetActionState}
        onConfirm={handleSkipStep}
        loading={actionLoading}
        fromStep={currentStepNo}
        toStep={currentStepNo + 1}
        stepNumberNameMap={stepNumberNameMap}
      />

      {/* Create Store — opens the shared store modal pre-filled from the CF, links storeId on success */}
      <CreateStoreForCf
        open={actionModal === "createStore"}
        onClose={() => setActionModal(null)}
        form={form}
        financial={financial}
        onSuccess={() => {
          setActionModal(null);
          loadFormDetails();
        }}
      />
    </Box>
  );
};

export default CfV2FormDetails;